import React, { useEffect, useLayoutEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowRight,
  FiBarChart2,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiChevronRight,
  FiClock,
  FiCode,
  FiCreditCard,
  FiDatabase,
  FiFileText,
  FiGrid,
  FiGlobe,
  FiHelpCircle,
  FiLayers,
  FiLayout,
  FiExternalLink,
  FiLock,
  FiMail,
  FiMapPin,
  FiMenu,
  FiMessageSquare,
  FiMonitor,
  FiRefreshCw,
  FiServer,
  FiSettings,
  FiShare2,
  FiShield,
  FiSmartphone,
  FiPhone,
  FiTrendingUp,
  FiUsers,
  FiX,
} from "react-icons/fi";
import useConsultPopup from "./useConsultPopup";
import ConsultPopup from "./ConsultPopup";
import slideTo, { rememberScroll, restoreScroll } from "./pageTransition";
import "./page-transition.css";
import "./upgrade-home.css";
import LocalizedContent, { useLocaleDocument } from "./localization";
import insights from "../data/insights.json";

const navItems = [
  { id: "dashboard", label: "대시보드", icon: FiGrid },
  { id: "serp", label: "세로 ERP", icon: FiLayers, badge: "도입 상담" },
  { id: "subscription", label: "맞춤 시스템 구독", icon: FiCode },
  { id: "grant", label: "정부지원금 구축", icon: FiCreditCard },
  { id: "maintenance", label: "기존 시스템 운영", icon: FiRefreshCw },
  { id: "global", label: "글로벌 운영", icon: FiGlobe },
  { id: "company", label: "솔컴바인 소개", icon: FiBriefcase },
  { id: "process", label: "도입 과정", icon: FiActivity },
  { id: "pricing", label: "서비스 안내", icon: FiBriefcase },
];

const systemAreas = [
  { icon: FiLayout, title: "업무 관리 시스템 (ERP·CRM)", to: "/company/portfolio/funeral-operations" },
  { icon: FiBarChart2, title: "관리자 페이지·대시보드" },
  { icon: FiSmartphone, title: "현장·직원용 모바일 앱", to: "/company/portfolio/facility-operations" },
  { icon: FiUsers, title: "고객용 웹·앱", to: "/company/portfolio/subscription-platform" },
  { icon: FiShare2, title: "협력사·거래처 포털", to: "/company/portfolio/facility-operations" },
  { icon: FiCalendar, title: "외부 연동 (알림톡·결제·엑셀)", to: "/subscription/cases" },
  { icon: FiDatabase, title: "데이터 이관" },
];

const grantSteps = [
  { no: "01", title: "계약", desc: "외주용역비 비목으로 집행할 수 있는 구축 계약서를 씁니다." },
  { no: "02", title: "설계", desc: "현장 인터뷰로 업무 흐름을 진단하고 화면·데이터 구조를 잡습니다." },
  { no: "03", title: "개발", desc: "협약기간에 맞춰 우선순위가 높은 화면부터 만듭니다." },
  { no: "04", title: "검수", desc: "일정이 밀리면 우선순위를 조정해서라도 기간 안에 검수를 마칩니다." },
  { no: "05", title: "운영 전환", desc: "협약이 끝나면 전담 운영 구독으로 이어서 운영합니다." },
];

const newSystemPoints = [
  { icon: FiShield, no: "01", title: "고객 앱과 데이터는 고객사 것", desc: "여러 고객사가 함께 쓰는 관리 시스템만 솔컴바인이 보유합니다." },
  { icon: FiDatabase, no: "02", title: "지금 쓰는 자료 그대로 이관", desc: "엑셀과 기존 프로그램의 데이터를 옮겨서 시작합니다." },
  { icon: FiLayers, no: "03", title: "급한 업무부터 순차 오픈", desc: "우선순위가 높은 화면부터 쓰면서 범위를 넓혀갑니다." },
  { icon: FiRefreshCw, no: "04", title: "만든 팀이 그대로 운영", desc: "구축 후 손을 떼는 외주가 아니라, 같은 팀이 운영과 개선을 이어받습니다." },
];

const colorThemes = [
  { id: "default", label: "기본 블루", color: "#2878FF" },
  { id: "mint", label: "민트", color: "#0F5C5A" },
  { id: "ocean", label: "딥 블루", color: "#16324F" },
  { id: "indigo", label: "인디고", color: "#5865F2" },
];

const locales = [{ id:"ko", label:"한국어" },{ id:"en", label:"English" },{ id:"zh", label:"中文" },{ id:"ja", label:"日本語" },{ id:"vi", label:"Tiếng Việt" },{ id:"fil", label:"Filipino" }];

// 글로벌 섹션은 역량을 주장하는 대신 실제 로케일 처리 결과를 그대로 보여준다.
const globalLocales = [
  { city: "서울", tz: "Asia/Seoul", locale: "ko-KR" },
  { city: "베이징", tz: "Asia/Shanghai", locale: "zh-CN" },
  { city: "도쿄", tz: "Asia/Tokyo", locale: "ja-JP" },
  { city: "하노이", tz: "Asia/Ho_Chi_Minh", locale: "vi-VN" },
  { city: "마닐라", tz: "Asia/Manila", locale: "en-PH" },
];

const formatLocalTime = (locale, timeZone, at) => new Intl.DateTimeFormat(locale, { timeZone, hour: "2-digit", minute: "2-digit" }).format(at);
const formatLocalDate = (locale, timeZone, at) => new Intl.DateTimeFormat(locale, { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(at);
const formatLocalNumber = (locale) => new Intl.NumberFormat(locale).format(1250000);

const processSteps = [
  ["01", "업무 진단", "현재 업무 방식과 사용 중인 시스템을 함께 분석합니다.", "현황 분석 · 핵심 과제 도출"],
  ["02", "우선순위 설계", "가장 시급하고 효과가 큰 업무부터 개발 범위를 정합니다.", "로드맵 · 화면 설계"],
  ["03", "구축과 적용", "현장 피드백을 반영하며 실제 사용하는 시스템을 만듭니다.", "단계별 개발 · 현장 검증"],
  ["04", "운영과 성장", "운영, 유지보수, 개선을 이어가며 회사와 함께 성장합니다.", "상시 운영 · 지속 개선"],
];

const heroSlides = [
  { image: "/images/company-system-design.jpg", alt: "고객사와 솔컴바인 담당자가 업무 시스템을 함께 설계하는 모습", label: "WORK AS ONE TEAM", title: <>회사의 일을 함께 이해하는<br/>전담 시스템 파트너</> },
  { image: "/images/funeral-erp-expertise.jpg", alt: "상조 업무 흐름과 ERP 화면을 함께 검토하는 전문가", label: "FUNERAL ERP EXPERTISE", title: <>상조 현장을 이해하고<br/>업무 전체를 연결합니다.</> },
  { image: "/images/dedicated-system-operation.jpg", alt: "기업 시스템의 운영 상태를 확인하는 전담 엔지니어", label: "DEDICATED OPERATION", title: <>구축 이후의 운영과 개선까지<br/>한 팀으로 이어갑니다.</> },
];

function ScrollLink({ to, className = "", children, onClick }) {
  const move = (event) => {
    event.preventDefault();
    document.getElementById(to)?.scrollIntoView({ behavior: "smooth", block: "start" });
    onClick?.();
  };
  return <a href={`#${to}`} className={className} onClick={move}>{children}</a>;
}

export default function UpgradeHome({ language = "ko" }) {
  const isDevicePreview = new URLSearchParams(window.location.search).get("devicePreview") === "1";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [colorTheme, setColorTheme] = useState(() => localStorage.getItem("sc-color-preview") || "default");
  const [fontScale, setFontScale] = useState(() => localStorage.getItem("sc-font-scale") || "normal");
  const [active, setActive] = useState("dashboard");
  const [heroSlide, setHeroSlide] = useState(0);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [localeClock, setLocaleClock] = useState(() => new Date());
  const [insightBannerDismissed, setInsightBannerDismissed] = useState(() => sessionStorage.getItem("sc-insight-banner-dismissed") === "1");
  const { consultOpen, setConsultOpen, consult, isSending, openConsult, closeConsult, updateConsult, submitConsult } = useConsultPopup();

  // Get latest insight within 7 days for banner
  const latestInsight = insights[0];
  const isInsightRecent = latestInsight && (() => {
    const insightDate = new Date(latestInsight.date);
    const now = new Date();
    const diffDays = (now - insightDate) / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  })();
  const showInsightBanner = language === "ko" && isInsightRecent && !insightBannerDismissed;

  const dismissInsightBanner = () => {
    sessionStorage.setItem("sc-insight-banner-dismissed", "1");
    setInsightBannerDismissed(true);
  };
  const navigate = useNavigate();
  const location = useLocation();
  useLocaleDocument(language, "home");

  const changeColorPreview = (nextTheme) => {
    localStorage.setItem("sc-color-preview", nextTheme);
    setColorTheme(nextTheme);
  };

  const changeFontScale = (nextScale) => {
    localStorage.setItem("sc-font-scale", nextScale);
    setFontScale(nextScale);
  };

  const themeSwitcher = (
    <div className="theme-switcher" role="group" aria-label="색상 테마 선택">
      <span>컬러</span>
      {colorThemes.map(({ id, label, color }) => <button type="button" key={id} className={colorTheme === id ? "is-active" : ""} onClick={() => changeColorPreview(id)} aria-label={`${label} 테마`} aria-pressed={colorTheme === id} title={label}><i style={{ background: color }}/></button>)}
    </div>
  );
  const fontSwitcher = (
    <div className="font-switcher" role="group" aria-label="글씨 크기 조절">
      <span>글씨</span>
      {[{ id: "small", label: "작게", text: "가−" }, { id: "normal", label: "기본", text: "가" }, { id: "large", label: "크게", text: "가+" }].map(({ id, label, text }) => <button type="button" key={id} className={fontScale === id ? "is-active" : ""} onClick={() => changeFontScale(id)} aria-label={`글씨 ${label}`} aria-pressed={fontScale === id} title={`글씨 ${label}`}>{text}</button>)}
    </div>
  );

  const changeLanguage = (nextLanguage) => {
    localStorage.setItem("sc-language", nextLanguage);
    // 한국어는 /로, 다른 언어는 /{언어}로 이동
    const path = nextLanguage === "ko" ? "/" : `/${nextLanguage}`;
    slideTo(navigate, path, undefined, nextLanguage === "en" ? "forward" : "back");
  };
  const renderLanguageSwitcher = () => <label className="language-select"><FiGlobe/><select value={language} onChange={(event) => changeLanguage(event.target.value)} aria-label="언어 선택">{locales.map(({id,label})=><option value={id} key={id} data-no-translate="true">{label}</option>)}</select></label>;

  const openMobilePreview = () => {
    setMobilePreviewOpen(true);
  };

  const goToCases = () => {
    rememberScroll();
    slideTo(navigate, `/${language}/subscription/cases`, undefined, "forward");
  };
  const goToGrantCases = () => {
    rememberScroll();
    slideTo(navigate, `/${language}/grant/cases`, undefined, "forward");
  };
  const goToSerpFeatures = () => {
    rememberScroll();
    slideTo(navigate, `/${language}/serp/features`, undefined, "forward");
  };
  const goToErp = () => {
    rememberScroll();
    slideTo(navigate, "/erp", undefined, "forward");
  };
  const goToTerms = () => {
    rememberScroll();
    slideTo(navigate, `/${language}/terms`, undefined, "forward");
  };
  const goToStory = () => {
    rememberScroll();
    slideTo(navigate, `/${language}/company`, undefined, "forward");
  };

  const goToOperationCases = () => {
    rememberScroll();
    slideTo(navigate, `/${language}/operation/cases`, undefined, "forward");
  };

  // 모바일 메뉴를 닫고 상담 팝업 열기
  const handleOpenConsult = () => {
    setMobileOpen(false);
    openConsult();
  };

  // 사례 페이지에서 돌아오면 보던 위치(구독 섹션)로 복귀시키고, 상담 요청이면 바로 연다.
  useLayoutEffect(() => {
    const { scrollTo, openConsult: shouldOpenConsult } = location.state || {};
    if (!scrollTo && !shouldOpenConsult) return;
    if (scrollTo) restoreScroll(scrollTo);
    if (shouldOpenConsult) setConsultOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate, setConsultOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-20% 0px -60%", threshold: [0.05, 0.25, 0.6] }
    );
    navItems.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setHeroSlide((current) => (current + 1) % heroSlides.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setLocaleClock(new Date()), 15000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobilePreviewOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobilePreviewOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobilePreviewOpen]);

  return (
    <LocalizedContent lang={language}><div className={`upgrade-home font-${fontScale} ${isDevicePreview ? "device-preview" : ""} ${colorTheme !== "default" ? `palette-theme ${colorTheme}-theme` : ""}`}>
      <header className="mobile-header">
        <a href="#dashboard" className="mobile-brand">SOL<span>COMBINE</span></a>
        <div className="mobile-header-actions">{renderLanguageSwitcher()}<button onClick={() => setMobileOpen((value) => !value)} aria-label="메뉴 열기">{mobileOpen ? <FiX /> : <FiMenu />}</button></div>
      </header>

      <aside className={`erp-sidebar ${mobileOpen ? "is-open" : ""}`}>
        <div className="brand-block">
          <a href="#dashboard" className="brand">SOL<span>COMBINE</span></a>
          <p>BUSINESS SYSTEM PARTNER</p>
        </div>
        <div className="mobile-preferences">{themeSwitcher}{fontSwitcher}</div>
        <nav aria-label="서비스 메뉴">
          <p className="nav-caption">SERVICE</p>
          {navItems.map(({ id, label, icon: Icon, badge }) => (
            <ScrollLink key={id} to={id} onClick={() => setMobileOpen(false)} className={`nav-item ${active === id ? "active" : ""}`}>
              <Icon />
              <span>{label}</span>
              {badge && <em>{badge}</em>}
              {!badge && <FiChevronRight className="nav-arrow" />}
            </ScrollLink>
          ))}
        </nav>
        {language === "ko" && (
          <Link to="/insights" className="sidebar-insight-link" onClick={() => setMobileOpen(false)}>
            <FiShield />
            <span>보안·IT 이슈 브리핑</span>
            <FiChevronRight />
          </Link>
        )}
        <div className="sidebar-contact">
          <FiMessageSquare />
          <div><strong>시스템이 필요하신가요?</strong><span>업무부터 함께 살펴봅니다.</span></div>
          <button type="button" onClick={handleOpenConsult} aria-label="도입 상담하기"><FiArrowRight /></button>
        </div>
        <div className="sidebar-status"><span><i /> 시스템 상담 운영 중</span><small>© SOLCOMBINE</small></div>
      </aside>

      {mobileOpen && <button className="mobile-overlay" aria-label="메뉴 닫기" onClick={() => setMobileOpen(false)} />}

      <nav className={`mobile-bottom-nav ${consultOpen || mobileOpen ? "is-hidden" : ""}`} aria-label="모바일 빠른 메뉴">
        <ScrollLink to="dashboard" className={active === "dashboard" ? "active" : ""}><FiGrid/><span>홈</span></ScrollLink>
        <ScrollLink to="subscription" className={["serp","subscription","grant","maintenance","global"].includes(active) ? "active" : ""}><FiBriefcase/><span>서비스</span></ScrollLink>
        <ScrollLink to="process" className={active === "process" ? "active" : ""}><FiActivity/><span>도입 과정</span></ScrollLink>
        <button type="button" onClick={openConsult}><FiMessageSquare/><span>상담</span></button>
      </nav>

      <div className="erp-workspace">
        <div className="erp-topbar">
          <div><span>솔컴바인</span><FiChevronRight /><strong>{navItems.find((item) => item.id === active)?.label || "대시보드"}</strong></div>
          <div className="top-actions">{renderLanguageSwitcher()}{themeSwitcher}{fontSwitcher}<button type="button" onClick={openMobilePreview} className="mobile-preview-button" title="모바일 보기"><FiSmartphone/><span>모바일 보기</span></button><button type="button" onClick={openConsult} className="top-consult">도입 상담 <FiArrowRight /></button></div>
        </div>

        <main>
          {showInsightBanner && latestInsight && (
            <Link to={`/insights/${latestInsight.id}`} className="insight-banner">
              <span className="insight-banner-label"><FiShield /> 보안 브리핑</span>
              <span className="insight-banner-title">{latestInsight.title}</span>
              <span className="insight-banner-cta">자세히 보기 <FiArrowRight /></span>
              <button
                type="button"
                className="insight-banner-close"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissInsightBanner(); }}
                aria-label="배너 닫기"
              >
                <FiX />
              </button>
            </Link>
          )}
          <section id="dashboard" className="hero-section">
            <div className="hero-copy">
              <div className="status-pill"><span /> 귀사의 시스템팀이 되어드립니다</div>
              <h1><span>직원 한 명의 비용으로,</span><em>전산팀 하나를 구독하세요.</em></h1>
              <p>개발자를 뽑고 팀을 꾸리는 대신, 설계부터 개발과 운영까지 한 팀에 맡기세요. 솔컴바인이 회사의 일하는 방식을 이해하고 필요한 시스템을 만들어 계속 운영합니다.</p>
              <div className="hero-actions">
                <ScrollLink to="subscription" className="primary-button">서비스 알아보기 <FiArrowRight /></ScrollLink>
                <button type="button" onClick={openConsult} className="secondary-button">우리 회사 상담하기</button>
              </div>
              <div className="hero-note"><FiShield /> 고객 데이터는 고객사의 자산으로 안전하게 보호됩니다.</div>
            </div>

            <div className="hero-visual">
              <figure className="hero-photo">
                {heroSlides.map((slide, index) => <img key={slide.image} className={index === heroSlide ? "is-active" : ""} src={slide.image} alt={index === heroSlide ? slide.alt : ""}/>) }
                <figcaption><span>{heroSlides[heroSlide].label}</span><strong>{heroSlides[heroSlide].title}</strong></figcaption>
                <div className="hero-slide-dots" aria-label="히어로 이미지 선택">{heroSlides.map((slide,index)=><button type="button" key={slide.image} className={index === heroSlide ? "active" : ""} onClick={()=>setHeroSlide(index)} aria-label={`${index+1}번째 이미지 보기`} aria-current={index === heroSlide ? "true" : undefined}/>)}</div>
              </figure>
              <div className="dashboard-preview" aria-label="서비스 적용 예시 대시보드">
              <div className="preview-head"><div><span>통합 운영 현황</span><small>서비스 적용 예시</small></div><button>이번 달 <FiChevronRight /></button></div>
              <div className="metrics-grid">
                <article><span className="metric-icon blue"><FiMonitor /></span><small>연결 업무</small><strong>12<em>개 영역</em></strong><p><b>+3</b> 이번 분기</p></article>
                <article><span className="metric-icon green"><FiClock /></span><small>자동화 시간</small><strong>168<em>시간</em></strong><p><b>24%</b> 업무 절감</p></article>
                <article><span className="metric-icon violet"><FiTrendingUp /></span><small>개선 진행률</small><strong>84<em>%</em></strong><p><b>정상</b> 계획대로 진행</p></article>
              </div>
              <div className="chart-card">
                <div className="chart-title"><div><strong>업무 효율 변화</strong><span>시스템 도입 이후</span></div><em>+34.8%</em></div>
                <div className="chart-area"><span className="chart-line" /><i style={{height:"24%"}}/><i style={{height:"30%"}}/><i style={{height:"42%"}}/><i style={{height:"49%"}}/><i style={{height:"64%"}}/><i style={{height:"76%"}}/><i style={{height:"88%"}}/></div>
                <div className="chart-labels"><span>1월</span><span>2월</span><span>3월</span><span>4월</span><span>5월</span><span>6월</span><span>7월</span></div>
              </div>
              <div className="preview-foot"><span><i/> 모든 시스템 정상</span><small>* 이해를 돕기 위한 데모 데이터입니다.</small></div>
              </div>
            </div>
          </section>

          <section className="product-overview" aria-labelledby="product-overview-title">
            <div className="product-overview-head">
              <div><span className="eyebrow">IT SUBSCRIPTION</span><h2 id="product-overview-title">개발 외주는 만들고 떠나지만,<br/>구독은 끝까지 함께합니다.</h2></div>
            </div>
            <div className="subscription-model-strip">
              <div><small>SOLCOMBINE IT SUBSCRIPTION</small><strong>업무 분석부터 지속 개선까지, 한 팀이 맡습니다.</strong></div>
              <ol>{["업무 분석","시스템 설계","맞춤 개발","현장 도입","전담 운영","지속 개선"].map((step,index)=><li key={step}><span>0{index+1}</span><b>{step}</b>{index < 5 && <FiChevronRight/>}</li>)}</ol>
            </div>
            <div className="product-lineup-label"><span>SUBSCRIPTION LINEUP</span></div>
            <div className="product-overview-grid">
              <ScrollLink to="serp" className="product-card product-sero">
                <div className="product-card-top"><em>상조 ERP가 필요하세요?</em></div>
                <div className="product-card-copy"><span className="product-card-icon"><FiLayers/></span><h3>세로 ERP</h3><ul className="product-features"><li><FiCheck/> 상담·고객관리</li><li><FiCheck/> 발주·재고관리와 정산</li></ul><div className="product-card-bottom"><span className="product-price"><small>STANDARD</small><b>무료</b></span><strong className="product-card-cta">상조 ERP 보기 <FiArrowRight/></strong></div></div>
              </ScrollLink>
              <ScrollLink to="subscription" className="product-card product-subscription">
                <div className="product-card-top"><em>초기 구축비가 부담이신가요?</em></div>
                <div className="product-card-copy"><span className="product-card-icon"><FiCode/></span><h3>맞춤 시스템 구독</h3><ul className="product-features"><li><FiCheck/> 초기 구축비 없이 시작</li><li><FiCheck/> 구축한 팀의 지속 운영과 개선</li></ul><div className="product-card-bottom"><span className="product-price"><small>MONTHLY PLAN</small><b>월 200만원부터</b></span><strong className="product-card-cta">구축 구독 보기 <FiArrowRight/></strong></div></div>
              </ScrollLink>
              <ScrollLink to="grant" className="product-card product-grant">
                <div className="product-card-top"><em>정부지원금을 받으셨나요?</em></div>
                <div className="product-card-copy"><span className="product-card-icon"><FiCreditCard/></span><h3>정부지원금 구축</h3><ul className="product-features"><li><FiCheck/> 외주용역비로 집행 가능</li><li><FiCheck/> 협약기간 내 검수 완료</li></ul><div className="product-card-bottom"><span className="product-price"><small>BUILD FEE</small><b>1,000만원부터</b></span><strong className="product-card-cta">지원금 구축 보기 <FiArrowRight/></strong></div></div>
              </ScrollLink>
              <ScrollLink to="maintenance" className="product-card product-operation">
                <div className="product-card-top"><em>이미 운영 중인 회사인가요?</em></div>
                <div className="product-card-copy"><span className="product-card-icon"><FiRefreshCw/></span><h3>전담 운영 구독</h3><ul className="product-features"><li><FiCheck/> 서버·보안·백업 운영</li><li><FiCheck/> 기능 개선·추가 개발</li></ul><div className="product-card-bottom"><span className="product-price"><small>MONTHLY PLAN</small><b>월 350만원부터</b></span><strong className="product-card-cta">운영 구독 보기 <FiArrowRight/></strong></div></div>
              </ScrollLink>
            </div>
            <div className="product-guide"><FiHelpCircle/><span><strong>어떤 방식이 맞는지 모르시겠나요?</strong> 현재 사용 중인 시스템과 불편한 업무만 알려주시면 적합한 시작점을 함께 찾습니다.</span><button type="button" onClick={openConsult} className="product-guide-arrow" aria-label="우리 회사에 맞는 방식 찾기" title="우리 회사에 맞는 방식 찾기"><svg viewBox="0 0 34 20" fill="none" aria-hidden="true"><path d="M3 10h27M23 3l7 7-7 7"/></svg></button></div>
            {language === "ko" && <div className="product-guide product-guide-erp"><FiLayers/><span><strong>상조가 아닌 회사의 ERP가 필요하신가요?</strong> 도입형·구독형·정부지원금 방식을 비교해 보세요.</span><button type="button" onClick={goToErp} className="product-guide-arrow" aria-label="맞춤 ERP 도입 방식 보기" title="맞춤 ERP 도입 방식 보기"><svg viewBox="0 0 34 20" fill="none" aria-hidden="true"><path d="M3 10h27M23 3l7 7-7 7"/></svg></button></div>}
          </section>

          <section id="serp" className="content-section serp-section">
            <div className="section-heading split-heading">
              <div><span className="eyebrow">SERO ERP</span><h2>상조 ERP가 필요하세요?</h2><p>상담부터 의전, 발주, 정산까지 하나로 연결하는 상조 전용 ERP입니다. 200명까지 무료로 바로 쓰는 표준형과 초기 비용 없이 회사 업무에 맞춰 구축하는 맞춤형 중에서 고를 수 있습니다.</p></div>
              <div className="serp-heading-links"><button type="button" onClick={goToSerpFeatures} className="serp-detail-link">표준형과 맞춤형 차이 · 견적 계산기 <FiArrowRight/></button><a className="serp-detail-link" href="https://erp.seroerp.com/login?utm_source=homepage" target="_blank" rel="noopener noreferrer">상조 ERP 데모 보기 <FiExternalLink/></a></div>
            </div>
            <div className="serp-options">
              <article>
                <span className="serp-option-tag">표준형</span>
                <div className="serp-option-body"><strong>무료</strong><small>사용자 200명까지 · 약정·시작 비용 없음 · 바로 사용</small></div>
                <button type="button" className="serp-option-primary" onClick={() => openConsult("세로 ERP 표준형 신청")}>표준형 신청 <FiArrowRight/></button>
              </article>
              <article>
                <span className="serp-option-tag">맞춤형</span>
                <div className="serp-option-body"><strong>월 50만원부터</strong><small>초기 비용 0원 · 50명까지 정액 · 회사 업무에 맞춰 전용 서버에 구축</small></div>
                <button type="button" className="serp-option-primary" onClick={() => openConsult("세로 ERP 맞춤형 상담")}>맞춤형 상담 <FiArrowRight/></button>
              </article>
            </div>
            <div className="serp-showcase">
              <figure className="serp-board serp-shot">
                <div className="serp-shot-bar" aria-hidden="true"><i/><i/><i/><span>세로 상조 ERP · 본사 현황</span></div>
                <video src="/videos/sero-erp-demo.mp4" poster="/images/sero-erp-demo-poster.jpg" width="1920" height="1080" autoPlay muted loop playsInline preload="metadata" aria-label="세로 상조 ERP 시연 영상: 본사 현황 대시보드와 주요 업무 화면"/>
              </figure>
            </div>
          </section>

          <section id="subscription" className="content-section subscription-section">
            <div className="section-heading"><span className="eyebrow">NEW SYSTEM SUBSCRIPTION</span><h2>초기 구축비가 부담이신가요?</h2><p><b>목돈이 없어도 지금 시작할 수 있습니다.</b> 정해진 패키지를 파는 것이 아니라, 회사가 일하는 방식 그대로 만들고 이어서 운영합니다. 급한 업무부터 순차로 열어, 쓰면서 범위를 넓혀갑니다.</p></div>
            <div className="subscription-layout">
              <article className="price-panel">
                <div className="price-panel-head"><span className="panel-tag">신규 시스템 구축 구독</span><em>5년 파트너십</em></div>
                <div className="price"><small>월</small><strong>200</strong><em>만원부터</em></div>
                <div className="contract-facts"><div><small>초기 구축비</small><strong>별도 없음</strong></div><div><small>서버·인프라</small><strong>구독료에 포함</strong></div><div><small>성과 연동 요금</small><strong>회사 유형별 산정</strong></div></div>
                <div className="price-included"><span>구독료에 포함되는 업무</span><ul><li><FiCheck/> 현장 인터뷰·업무 흐름 진단</li><li><FiCheck/> 화면·데이터 구조 설계</li><li><FiCheck/> 우선순위 기반 맞춤 개발</li><li><FiCheck/> 기존 데이터 이관·외부 연동</li><li><FiCheck/> 서버 운영·보안·백업</li><li><FiCheck/> 유지보수·지속 개선</li></ul></div>
                <button type="button" onClick={openConsult} className="light-button">도입 가능성 상담하기 <FiArrowRight/></button>
                <small className="price-footnote">* 구독료는 부가세 별도이며, 실제 금액과 개발 범위는 사전 진단 후 확정됩니다. 알림톡·AI 토큰 등 사용량 기반 부가서비스 요금은 고객사 부담입니다.</small>
              </article>


              <div className="subscription-right">
                <div className="new-system-points">
                  <div className="points-head"><span className="band-tag">신규 도입</span><strong>처음 도입할 때 걱정되는 부분, 이렇게 해결합니다</strong></div>
                  <ol>
                    {newSystemPoints.map(({ icon: Icon, no, title, desc }) => (
                      <li key={no}>
                        <span className="point-marker"><i><Icon/></i><small>{no}</small></span>
                        <span className="point-copy"><strong>{title}</strong><p>{desc}</p></span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="build-areas">
                  <small>구독으로 만드는 것</small>
                  {systemAreas.map(({ icon: Icon, title, to }) => to
                    ? <Link key={title} to={to} className="build-area-link"><Icon/> {title}</Link>
                    : <span key={title}><Icon/> {title}</span>
                  )}
                  <em>그 외 필요한 무엇이든</em>
                </div>

                <button type="button" onClick={goToCases} className="case-preview">
                  <span className="case-preview-head">
                    <em>CONTRACT EXAMPLES</em>
                    <strong>성과 연동 요금, 어떻게 정하나요?</strong>
                    <small>회사 유형별 계약 구조 예시 4가지</small>
                  </span>
                  <span className="case-preview-arrow" aria-hidden="true">
                    <svg viewBox="0 0 34 20" fill="none">
                      <path d="M3 10h27M23 3l7 7-7 7" />
                    </svg>
                  </span>
                  <span className="case-preview-peek" aria-hidden="true" />
                </button>
              </div>
            </div>
          </section>

          <section id="grant" className="content-section grant-section">
            <div className="section-heading"><span className="eyebrow">STARTUP GRANT</span><h2>정부지원금을 받으셨나요?</h2><p><b>초기창업패키지 등 정부지원금으로 시스템을 만들 수 있습니다.</b> 협약기간 안에 완성해 검수까지 마치고, 자금 집행에 필요한 증빙 서류를 함께 준비해 드립니다.</p></div>
            <div className="grant-band">
              <div className="grant-band-head">
                <div className="grant-facts">
                  <div><small>구축비 (VAT 별도)</small><strong>3,000만원부터</strong></div>
                  <div><small>기본 범위</small><strong>모든 시스템</strong></div>
                  <div><small>집행 비목</small><strong>외주용역비</strong></div>
                  <div><small>전용 개발분</small><strong>고객사 귀속</strong></div>
                </div>
                <div className="grant-actions"><button type="button" onClick={openConsult} className="grant-cta">지원금 도입 상담하기 <FiArrowRight/></button></div>
              </div>
              <ol className="grant-steps">
                {grantSteps.map(({ no, title, desc }) => (
                  <li key={no}><span>{no}</span><strong>{title}</strong><p>{desc}</p></li>
                ))}
              </ol>
              <div className="grant-areas">
                <small>지원금으로 만드는 것</small>
                {systemAreas.map(({ icon: Icon, title }) => <span key={title}><Icon/> {title}</span>)}
                <em>그 외 필요한 무엇이든</em>
              </div>
              <div className="grant-footer"><p className="grant-note"><FiHelpCircle/> 구축비는 부가세 별도이며, 화면 수와 외부 연동 범위에 따라 달라집니다. 지원사업과 주관기관에 따라 집행 가능한 비목과 증빙 요건이 다르니 계약 전에 함께 확인해 드립니다.</p><button type="button" onClick={goToGrantCases} className="grant-secondary" aria-label="지원사업별 집행 예시 보기" title="지원사업별 집행 예시 보기"><svg viewBox="0 0 34 20" fill="none" aria-hidden="true"><path d="M3 10h27M23 3l7 7-7 7"/></svg></button></div>
            </div>
          </section>

          <section id="maintenance" className="content-section maintenance-section">
            <div className="maintenance-card">
              <div className="maintenance-copy"><span className="eyebrow">SYSTEM OPERATION</span><h2>이미 운영 중인 회사인가요?</h2><p>운영만 맡기는 것이 아니라, 개선까지 이어지는 전담팀입니다.</p><div className="maintenance-facts"><span><small>대상</small><strong>운영 중인 웹·앱·ERP</strong></span><span><small>기본 범위</small><strong>운영 + 개선 개발</strong></span><span><small>협업 방식</small><strong>월간 계획·결과 보고</strong></span></div></div>
              <div className="maintenance-price"><span>전담 운영 + 추가 개발</span><div><small>월</small><strong>350</strong><em>만원부터</em></div><button type="button" onClick={openConsult}>우리 시스템 진단받기 <FiArrowRight/></button><small className="maintenance-price-note">* 부가세 별도이며, 단일 업무 시스템의 기본 운영 예시입니다.</small></div>
              <div className="operation-list">{[[FiActivity,"장애 모니터링·복구"],[FiServer,"서버·백업 운영"],[FiShield,"보안 업데이트"],[FiCode,"기능 개선·추가 개발"]].map(([Icon,title],index)=><div key={title}><span><Icon/></span><div><small>0{index+1}</small><strong>{title}</strong></div></div>)}</div>
              <div className="maintenance-assurance"><FiCheck/><span><strong>월 비용에 포함</strong>합의된 범위의 유지보수와 추가 개발</span><i/><FiHelpCircle/><span><strong>별도 사전 협의</strong>대규모 신규 구축 또는 운영 범위를 크게 벗어나는 개발</span></div>
              <button type="button" onClick={goToOperationCases} className="maintenance-case-link">
                <span><em>OPERATION CONTRACT EXAMPLES</em><strong>시스템 규모별 운영 계약은 어떻게 달라지나요?</strong><small>단일 시스템부터 전담팀형까지 계약 예시 4가지</small></span>
                <span className="case-preview-arrow" aria-hidden="true"><svg viewBox="0 0 34 20" fill="none"><path d="M3 10h27M23 3l7 7-7 7" /></svg></span>
                <span className="case-preview-peek" aria-hidden="true" />
              </button>
            </div>
          </section>

          <section id="global" className="content-section global-section">
            <div className="global-intro">
              <div className="section-heading"><span className="eyebrow">GLOBAL SYSTEM SUPPORT</span><h2>국가와 언어가 달라도,<br/>하나의 시스템으로 운영할 수 있습니다.</h2></div>
              <button type="button" onClick={openConsult}>글로벌 시스템 상담하기 <FiArrowRight/></button>
            </div>

            <div className="global-board">
            <div className="global-demo">
              <div className="global-demo-head">
                <span>LOCALE PREVIEW</span>
                <strong>언어를 바꾸면 이 페이지 전체가 함께 바뀝니다.</strong>
              </div>
              <div className="global-demo-langs" role="group" aria-label="언어 미리보기">
                {locales.map(({ id, label }) => <button type="button" key={id} className={language === id ? "is-active" : ""} onClick={() => changeLanguage(id)} aria-pressed={language === id} data-no-translate="true">{label}</button>)}
              </div>
              <div className="global-demo-table">
                <div className="global-demo-row is-head"><span/><small>현지 시각</small><small>날짜 표기</small><small>숫자 표기</small></div>
                {globalLocales.map(({ city, tz, locale }) => (
                  <div className="global-demo-row" key={city}>
                    <span>{city}</span>
                    <b>{formatLocalTime(locale, tz, localeClock)}</b>
                    <em>{formatLocalDate(locale, tz, localeClock)}</em>
                    <em>{formatLocalNumber(locale)}</em>
                  </div>
                ))}
              </div>
            </div>

            <div className="global-grid">{[
              [FiGlobe,"다국어 사용자 경험","사용자와 관리자 화면의 언어를 계정별로 제공합니다."],
              [FiClock,"시간대·통화·날짜","국가별 시간대, 통화, 숫자와 날짜 표기 방식을 반영합니다."],
              [FiUsers,"본사·해외 지사 통합","국가별 권한은 분리하면서 필요한 데이터는 본사에서 통합 관리합니다."],
              [FiShield,"글로벌 운영 대응","배포, 모니터링, 장애 대응과 개선 요청을 하나의 운영 체계로 관리합니다."],
            ].map(([Icon,title,desc],index)=><article key={title}><span><Icon/></span><small>0{index+1}</small><h3>{title}</h3><p>{desc}</p></article>)}</div>
            <div className="global-scope">
              <div className="global-scope-head"><FiHelpCircle/><strong>해외 도입 시 사전 진단에서 함께 확정하는 것</strong></div>
              <ul>
                {[
                  [FiCreditCard, "결제·정산", "현지 결제수단 연동, 통화 표기, 환율 기준"],
                  [FiFileText, "세금·증빙", "현지 세율, 인보이스·세금계산서 양식"],
                  [FiDatabase, "데이터 소재지", "서버 위치, 개인정보 국외 이전 절차"],
                  [FiShield, "현지 법규", "국가별 규제와 필수 신고 사항"],
                ].map(([Icon, title, desc]) => <li key={title}><Icon/><span><strong>{title}</strong><small>{desc}</small></span></li>)}
              </ul>
            </div>
            </div>
          </section>

          <section id="company" className="content-section company-section">
            <div className="company-intro">
              <div className="section-heading">
                <span className="eyebrow">ABOUT SOLCOMBINE</span>
                <h2>상조 산업에서 시작해,<br/>기업의 모든 업무를 연결합니다.</h2>
              </div>
            </div>
            <div className="company-values">
              <article><div className="company-value-image"><img src="/images/funeral-erp-expertise.jpg" alt="상조 ERP 업무 화면을 함께 검토하는 전문가"/><span>01</span></div><div className="company-value-copy"><FiLayers/><h3>상조 ERP 전문성</h3><p>상담부터 의전·발주·정산까지 상조 업무 전체를 시스템으로 만들어왔습니다.</p></div></article>
              <article><div className="company-value-image"><img src="/images/company-system-design.jpg" alt="기업 담당자와 맞춤 시스템을 설계하는 모습"/><span>02</span></div><div className="company-value-copy"><FiCode/><h3>기업 맞춤 시스템 구축·구독</h3><p>함께 쓰는 관리 시스템 위에 회사마다 필요한 부분을 새로 만듭니다. 그래서 구축비 없이 시작할 수 있습니다.</p></div></article>
              <article><div className="company-value-image"><img src="/images/dedicated-system-operation.jpg" alt="기업 시스템 상태를 전담 모니터링하는 담당자"/><span>03</span></div><div className="company-value-copy"><FiRefreshCw/><h3>기존 시스템 전담 운영</h3><p>남이 만든 시스템도 넘겨받아 운영하고, 합의된 범위 안에서 개선 개발까지 이어갑니다.</p></div></article>
            </div>

            <div className="company-info-bar">
              <div className="company-info-head"><div><span>CONTACT SOLCOMBINE</span><strong>필요한 시스템에 대해 편하게 이야기해주세요.</strong></div>            <button type="button" onClick={goToStory} className="company-story-button" aria-label="회사 이야기 보기" title="회사 이야기 보기"><svg viewBox="0 0 34 20" fill="none" aria-hidden="true"><path d="M3 10h27M23 3l7 7-7 7"/></svg></button></div>
              <a className="company-info-item" href="tel:1800-8477"><i><FiPhone/></i><span><small>대표전화</small><strong>1800-8477</strong><em>평일 09:00 ~ 18:00</em></span><FiChevronRight/></a>
              <a className="company-info-item" href="mailto:sckcs@solcombine.com"><i><FiMail/></i><span><small>이메일 문의</small><strong>sckcs@solcombine.com</strong><em>내용 확인 후 담당자가 연락드립니다.</em></span><FiChevronRight/></a>
              <a className="company-info-item" href="https://map.naver.com/p/search/%EC%84%9C%EC%9A%B8%EC%8B%9C%20%EA%B0%95%EC%84%9C%EA%B5%AC%20%EB%A7%88%EA%B3%A1%EC%A4%91%EC%95%995%EB%A1%9C1%EA%B8%B8%2020%20%EB%B3%B4%ED%83%80%EB%8B%89%EB%B9%84%EC%A6%88%ED%83%80%EC%9B%8C" target="_blank" rel="noreferrer"><i><FiMapPin/></i><span><small>네이버 지도에서 오시는 길</small><strong>서울시 강서구 마곡중앙5로1길 20</strong><em>보타닉비즈타워 519호</em></span><FiChevronRight/></a>
            </div>
          </section>

          <section id="process" className="content-section process-section">
            <div className="process-intro">
              <div className="section-heading"><span className="eyebrow">HOW WE WORK</span><h2>시스템보다 먼저,<br/>회사의 일을 이해합니다.</h2></div>
              <figure className="process-visual"><img src="/images/system-consulting-v3.jpg" alt="고객사 담당자와 시스템 전문가들이 업무 대시보드를 함께 검토하는 모습"/><figcaption><span>WORK WITH YOUR TEAM</span><strong>고객사의 언어로 듣고,<br/>시스템의 언어로 만듭니다.</strong></figcaption></figure>
            </div>
            <div className="process-grid">{processSteps.map(([number,title,,output])=><article key={number}><span>{number}</span><small>STEP {number}</small><h3>{title}</h3><em>{output}</em><i/></article>)}</div>
          </section>

          <section id="pricing" className="content-section policy-section">
            <div className="section-heading"><span className="eyebrow">CLEAR & TRANSPARENT</span><h2>오래 함께하기 위해<br/>권리와 범위를 투명하게.</h2><p>구독료는 소프트웨어의 분할 구매대금이 아닌, 계약기간 동안 시스템을 구축하고 이용하며 운영·개선하기 위한 서비스 이용료입니다.</p></div>
            <div className="policy-grid"><article><FiLock/><h3>시스템 이용권</h3><p>구독은 계약기간 동안 시스템을 이용하고 60개월 이후 그때의 규모로 재산정해 갱신합니다. 구축 계약은 완료 후 전담 운영 구독으로 이어갈 수 있습니다.</p></article><article><FiCode/><h3>소스코드 소유권</h3><p>고객 앱은 고객사 것입니다. 여러 회사가 함께 쓰는 관리 시스템만 솔컴바인이 갖습니다. 저희가 서비스를 못 하게 되면 계약서에 정한 대로 넘겨드립니다. 정부지원금 구축은 전용 개발분도 고객사 것입니다.</p></article><article><FiDatabase/><h3>데이터 이동권</h3><p>고객사가 제공하고 입력한 데이터는 고객사 소유이며 종료 시 합의된 형식으로 안전하게 이전합니다.</p></article></div>
            <p className="policy-note"><FiHelpCircle/> 실제 개발 범위, 성과 연동 기준, 중도해지 및 갱신 조건은 사전 진단 후 개별 계약서에 명확히 정합니다.<button type="button" onClick={goToTerms} className="policy-terms-link" aria-label="상품별 계약 조건 보기" title="상품별 계약 조건 보기"><svg viewBox="0 0 34 20" fill="none" aria-hidden="true"><path d="M3 10h27M23 3l7 7-7 7"/></svg></button></p>
          </section>

          <section id="contact" className="contact-section">
            <div><span className="eyebrow">LET'S BUILD TOGETHER</span><h2>지금 회사에 필요한 시스템부터<br/>함께 이야기해볼까요?</h2></div>
            <div className="contact-actions"><button type="button" onClick={openConsult} className="contact-primary">도입 상담 시작하기 <FiArrowRight/></button><a href="mailto:sckcs@solcombine.com" className="contact-mail">sckcs@solcombine.com</a></div>
          </section>
        </main>
        <footer className="upgrade-footer"><a href="#dashboard" className="brand">SOL<span>COMBINE</span></a><div className="footer-info"><p>회사의 일하는 방식을 시스템으로 만듭니다.</p><small>주식회사 솔컴바인 · 공동대표 최진형, 심재문 · 사업자등록번호 801-86-02860 · 통신판매업신고 제2026-서울강서-0948호</small><small>서울특별시 강서구 마곡중앙5로1길 20 보타닉비즈타워 519호 · 고객센터 1800-8477 · sckcs@solcombine.com · <a href="https://blog.naver.com/solcombine" target="_blank" rel="noopener noreferrer">블로그</a> · <Link to="/insights">보안 이슈</Link></small></div><span>© SOLCOMBINE. All rights reserved.</span></footer>
      </div>

      {mobilePreviewOpen && (
        <div className="mobile-preview-modal" role="dialog" aria-modal="true" aria-label="모바일 보기">
          <button type="button" className="mobile-preview-backdrop" onClick={() => setMobilePreviewOpen(false)} aria-label="닫기" />
          <div className="mobile-device-shell">
            <div className="mobile-device-speaker" aria-hidden="true"><i/><span/></div>
            <iframe title="모바일 보기" src={`${window.location.pathname}?devicePreview=1#${active}`} />
            <i className="mobile-device-home" aria-hidden="true" />
          </div>
          <button type="button" className="mobile-preview-close" onClick={() => setMobilePreviewOpen(false)} aria-label="닫기"><FiX/></button>
        </div>
      )}

      <ConsultPopup
        consultOpen={consultOpen}
        consult={consult}
        isSending={isSending}
        closeConsult={closeConsult}
        updateConsult={updateConsult}
        submitConsult={submitConsult}
      />
    </div></LocalizedContent>
  );
}
